/**
 * Local storage service for user settings
 * Provides persistence for user preferences using localStorage
 */

import { logger } from '../utils/logger.js'
import type { UserSettings } from '../database/schema.js'

const log = logger.create('storage')

const SETTINGS_STORAGE_KEY = 'cairn_user_settings'

export interface StoredSettings extends UserSettings {
  lastUpdated: number
}

export class SettingsStorage {
  /**
   * Load settings from localStorage
   */
  loadSettings(): UserSettings | null {
    try {
      const stored = localStorage.getItem(SETTINGS_STORAGE_KEY)
      if (!stored) {
        return null
      }

      const parsed: StoredSettings = JSON.parse(stored)

      // Remove the lastUpdated timestamp before returning
      const { lastUpdated: _lastUpdated, ...settings } = parsed
      return settings
    } catch (error) {
      log.error('Failed to load settings from localStorage:', error)
      return null
    }
  }

  /**
   * Save settings to localStorage
   */
  saveSettings(settings: UserSettings): boolean {
    try {
      const toStore: StoredSettings = {
        ...settings,
        lastUpdated: Date.now(),
      }

      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(toStore))
      return true
    } catch (error) {
      log.error('Failed to save settings to localStorage:', error)
      return false
    }
  }

  /**
   * Clear all settings from localStorage
   */
  clearSettings(): boolean {
    try {
      localStorage.removeItem(SETTINGS_STORAGE_KEY)
      return true
    } catch (error) {
      log.error('Failed to clear settings from localStorage:', error)
      return false
    }
  }

  /**
   * Get default settings
   */
  getDefaultSettings(): UserSettings {
    return {
      unitSystem: 'imperial',
      theme: 'auto',
      coordinateFormat: 'DD',
      // Auto-calculated from unitSystem
      elevationUnit: 'feet',
      distanceUnit: 'miles',
      areaUnit: 'acres',
      temperatureUnit: 'fahrenheit',
      syncPreferences: {
        wifi: true,
        cellular: true,
      },
      notifications: {
        emergency: true,
        messages: true,
      },
      privacy: {
        shareLocation: 'approximate',
      },
      tracking: {
        trackQuality: 'standard',
      },
    }
  }

  /**
   * Get settings with defaults applied
   */
  getSettingsWithDefaults(): UserSettings {
    const stored = this.loadSettings()
    const defaults = this.getDefaultSettings()

    if (!stored) {
      return defaults
    }

    // Merge with defaults to ensure all properties are present
    const unitSystem = stored.unitSystem
    const isImperial = unitSystem === 'imperial'

    return {
      unitSystem,
      theme: stored.theme,
      coordinateFormat: stored.coordinateFormat,
      coordinatePrecision: {
        ddDecimals: stored.coordinatePrecision?.ddDecimals ?? 5,
        dmsSecondDecimals: stored.coordinatePrecision?.dmsSecondDecimals ?? 0,
        directionFormat: stored.coordinatePrecision?.directionFormat ?? 'signed',
        mgrsPrecision: stored.coordinatePrecision?.mgrsPrecision ?? 5,
        utmShowZone: stored.coordinatePrecision?.utmShowZone ?? true,
      },
      elevationUnit: isImperial ? 'feet' : 'meters',
      distanceUnit: isImperial ? 'miles' : 'kilometers',
      areaUnit: isImperial ? 'acres' : 'hectares',
      temperatureUnit: isImperial ? 'fahrenheit' : 'celsius',
      syncPreferences: {
        wifi: stored.syncPreferences?.wifi ?? defaults.syncPreferences!.wifi,
        cellular: stored.syncPreferences?.cellular ?? defaults.syncPreferences!.cellular,
      },
      notifications: {
        emergency: stored.notifications?.emergency ?? defaults.notifications!.emergency,
        messages: stored.notifications?.messages ?? defaults.notifications!.messages,
      },
      privacy: {
        shareLocation: stored.privacy?.shareLocation ?? defaults.privacy!.shareLocation,
      },
      tracking: {
        trackQuality: stored.tracking?.trackQuality ?? defaults.tracking!.trackQuality,
      },
    }
  }
}
